import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { MultiSelect } from 'primereact/multiselect'
import { classNames } from 'primereact/utils'
import {
  StockItemTypeContractCreateResultDTO,
  StockItemTypeContractFormErrors,
  StockItemTypeContractFormField,
  StockItemTypeContractListItemDTO,
  StockItemTypeContractOptionDTO,
  StockItemTypeContractOptionsDTO,
  StockItemTypeContractPlanOptionDTO,
} from '../../dto/stockItemTypeContract/StockItemTypeContractDTO'
import {
  createStockItemTypeContracts,
  getStockItemTypeContractPlans,
  parseOverlapConflicts,
  updateStockItemTypeContract,
} from '../../services/StockItemTypeContractController'
import { ApiErrorResponse, parseApiError } from '../../services/apiError'
import { fromDateToString } from '../../utils/utils'
import { parseIsoDate } from './stockItemTypeContractFormat'
import OverlappingContractsModal from './components/OverlappingContractsModal'
import PercentageInput from './components/PercentageInput'

interface StockItemTypeContractFormViewProps {
  /** Contrato a editar, o null para un alta. */
  contract: StockItemTypeContractListItemDTO | null
  healthEntities: StockItemTypeContractOptionDTO[]
  options: StockItemTypeContractOptionsDTO
  /** Vuelve al listado sin guardar (ya confirmado si había cambios). */
  onBack: () => void
  /** `result` viene en un alta (cuántos contratos se crearon y cuántos se omitieron) y es null en una edición. */
  onSaved: (isEdition: boolean, result: StockItemTypeContractCreateResultDTO | null) => void
  /** Error general (sin campo puntual): la vista lo muestra como card flotante. */
  onError: (message: string) => void
  /** El contrato ya no se puede editar (se desactivó o no existe): la vista cierra y refresca. */
  onStale: (message: string) => void
}

interface FormState {
  healthEntityIds: number[]
  healthEntityPlanIds: number[]
  stockItemTypeIds: number[]
  contractAttentionModalityIds: number[]
  percentage: number | null
  validFrom: Date | null
  validUntil: Date | null
}

type EditableField = keyof FormState

// Mismos textos que el backend (StockItemTypeContractMessages): dicen qué hacer, no qué salió mal.
const MESSAGES = {
  healthEntityRequired: 'Seleccioná al menos una obra social',
  percentageRequired: 'Completá el porcentaje',
  percentageOutOfRange: 'Ingresá un porcentaje entre 0 y 100',
  validFromRequired: 'Completá la fecha desde',
  validUntilBeforeValidFrom: 'Ingresá una fecha hasta igual o posterior a la fecha desde',
}

const FIELD_INPUT_IDS: Record<StockItemTypeContractFormField, string> = {
  healthEntityIds: 'stc-health-entities',
  healthEntityPlanIds: 'stc-health-entity-plans',
  stockItemTypeIds: 'stc-stock-item-types',
  contractAttentionModalityIds: 'stc-modalities',
  percentage: 'stc-percentage',
  validFrom: 'stc-valid-from',
  validUntil: 'stc-valid-until',
  validity: 'stc-valid-from',
  combinations: 'stc-health-entities',
}

const FIELD_ORDER: StockItemTypeContractFormField[] = [
  'healthEntityIds',
  'healthEntityPlanIds',
  'stockItemTypeIds',
  'contractAttentionModalityIds',
  'percentage',
  'validFrom',
  'validUntil',
  'validity',
  'combinations',
]

const API_FIELDS = new Set<string>(FIELD_ORDER)

function isFormField(field: string): field is StockItemTypeContractFormField {
  return API_FIELDS.has(field)
}

function today(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function emptyForm(): FormState {
  return {
    healthEntityIds: [],
    healthEntityPlanIds: [],
    stockItemTypeIds: [],
    contractAttentionModalityIds: [],
    percentage: null,
    validFrom: today(),
    validUntil: null,
  }
}

function formFromContract(contract: StockItemTypeContractListItemDTO): FormState {
  return {
    healthEntityIds: [contract.healthentity],
    healthEntityPlanIds: contract.healthentityplan == null ? [] : [contract.healthentityplan],
    stockItemTypeIds: contract.stockitemtype == null ? [] : [contract.stockitemtype],
    contractAttentionModalityIds: contract.contractattentionmodality == null ? [] : [contract.contractattentionmodality],
    percentage: contract.percentage,
    validFrom: parseIsoDate(contract.validfrom),
    validUntil: contract.validuntil ? parseIsoDate(contract.validuntil) : null,
  }
}

function validate(form: FormState, isEdition: boolean): StockItemTypeContractFormErrors {
  const errors: StockItemTypeContractFormErrors = {}
  if (!isEdition && form.healthEntityIds.length === 0) errors.healthEntityIds = MESSAGES.healthEntityRequired
  if (form.percentage == null) {
    errors.percentage = MESSAGES.percentageRequired
  } else if (form.percentage < 0 || form.percentage > 100) {
    errors.percentage = MESSAGES.percentageOutOfRange
  }
  if (form.validFrom == null) {
    errors.validFrom = MESSAGES.validFromRequired
  } else if (form.validUntil != null && form.validUntil < form.validFrom) {
    errors.validUntil = MESSAGES.validUntilBeforeValidFrom
  }
  return errors
}

/**
 * Cantidad de contratos que genera la selección. Misma regla que el backend: cada obra social se
 * combina con sus planes elegidos, o con "todos los planes" si no se eligió ninguno suyo; tipos y
 * modalidades vacíos cuentan como uno ("todos").
 */
function countCombinations(form: FormState, plans: StockItemTypeContractPlanOptionDTO[]): number {
  const types = Math.max(form.stockItemTypeIds.length, 1)
  const modalities = Math.max(form.contractAttentionModalityIds.length, 1)
  const selectedPlans = plans.filter((plan) => form.healthEntityPlanIds.includes(plan.id))
  return form.healthEntityIds.reduce((total, healthEntityId) => {
    const plansOfEntity = selectedPlans.filter((plan) => plan.healthEntityId === healthEntityId).length
    return total + Math.max(plansOfEntity, 1) * types * modalities
  }, 0)
}

function toFieldErrors(apiError: ApiErrorResponse): StockItemTypeContractFormErrors {
  const errors: StockItemTypeContractFormErrors = {}
  for (const detail of apiError.details ?? []) {
    if (isFormField(detail.field) && !errors[detail.field]) {
      errors[detail.field] = detail.message
    }
  }
  return errors
}

/** Mensaje de los errores que son de un grupo de campos (no de un campo puntual): no se les manda el foco. */
const GROUP_ERROR_MESSAGE_IDS: Partial<Record<StockItemTypeContractFormField, string>> = {
  validity: 'stc-validity-error',
  combinations: 'stc-combinations-error',
}

function focusFirstError(errors: StockItemTypeContractFormErrors) {
  const first = FIELD_ORDER.find((field) => errors[field])
  if (!first) return
  window.setTimeout(() => {
    // Un error de grupo (por ejemplo, vigencias superpuestas) solo se hace visible: enfocar "Válido desde"
    // abriría su calendario sin que el usuario lo haya pedido.
    const groupMessageId = GROUP_ERROR_MESSAGE_IDS[first]
    if (groupMessageId) {
      document.getElementById(groupMessageId)?.scrollIntoView({ block: 'nearest' })
      return
    }
    const element = document.getElementById(FIELD_INPUT_IDS[first])
    if (element instanceof HTMLElement) {
      element.scrollIntoView({ block: 'center' })
      element.focus()
    }
  }, 0)
}

function FieldError({ id, message }: Readonly<{ id: string; message?: string }>) {
  if (!message) return null
  return (
    <small id={id} className='stc-field-error'>
      <i className='pi pi-exclamation-triangle' aria-hidden='true' />
      {message}
    </small>
  )
}

function ReadOnlyData({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className='stc-readonly-data'>
      <span className='stc-readonly-label'>{label}</span>
      <span className='stc-readonly-value'>{value || '—'}</span>
    </div>
  )
}

/** Representación comparable del formulario, para saber si tiene cambios sin guardar. */
function snapshotOf(form: FormState): string {
  return JSON.stringify({
    ...form,
    validFrom: form.validFrom ? fromDateToString(form.validFrom) : null,
    validUntil: form.validUntil ? fromDateToString(form.validUntil) : null,
  })
}

export default function StockItemTypeContractFormView({
  contract,
  healthEntities,
  options,
  onBack,
  onSaved,
  onError,
  onStale,
}: Readonly<StockItemTypeContractFormViewProps>) {
  const isEdition = contract != null
  const [form, setForm] = useState<FormState>(() => (contract ? formFromContract(contract) : emptyForm()))
  const initialSnapshot = useRef(snapshotOf(form))
  /** Siempre el último estado del formulario, para leerlo desde callbacks diferidos. */
  const latestForm = useRef(form)
  latestForm.current = form
  const [errors, setErrors] = useState<StockItemTypeContractFormErrors>({})
  /** Combinaciones que chocan con un contrato activo, según el último intento de guardar. */
  const [conflicts, setConflicts] = useState<StockItemTypeContractListItemDTO[]>([])
  const [viewingConflicts, setViewingConflicts] = useState(false)
  const [plans, setPlans] = useState<StockItemTypeContractPlanOptionDTO[]>([])
  const [loadingPlans, setLoadingPlans] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmingLeave, setConfirmingLeave] = useState(false)

  // Los planes dependen de las obras sociales elegidas: no se cargan hasta que haya alguna (principio 4 del design system).
  const healthEntityKey = form.healthEntityIds.join(',')
  useEffect(() => {
    if (isEdition) return
    if (form.healthEntityIds.length === 0) {
      setPlans([])
      setForm((current) => (current.healthEntityPlanIds.length === 0 ? current : { ...current, healthEntityPlanIds: [] }))
      return
    }

    let cancelled = false
    setLoadingPlans(true)
    getStockItemTypeContractPlans(form.healthEntityIds)
      .then((loaded) => {
        if (cancelled) return
        setPlans(loaded)
        setForm((current) => {
          const valid = current.healthEntityPlanIds.filter((id) => loaded.some((plan) => plan.id === id))
          return valid.length === current.healthEntityPlanIds.length ? current : { ...current, healthEntityPlanIds: valid }
        })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        parseApiError(error)
        setPlans([])
        onError('No se pudieron cargar los planes de las obras sociales. Intentá de nuevo.')
      })
      .finally(() => {
        if (!cancelled) setLoadingPlans(false)
      })

    return () => {
      cancelled = true
    }
  }, [isEdition, healthEntityKey])

  const planOptions = useMemo(() => {
    const showHealthEntity = form.healthEntityIds.length > 1
    return plans.map((plan) => ({
      id: plan.id,
      label: showHealthEntity ? `${plan.name} (${plan.healthEntityName})` : plan.name,
    }))
  }, [plans, form.healthEntityIds.length])

  const combinations = useMemo(() => countCombinations(form, plans), [form, plans])

  function updateField<K extends EditableField>(field: K, value: FormState[K]) {
    const next: FormState = { ...form, [field]: value }
    setForm(next)

    // Cambiar la selección o las fechas descarta el aviso de choques: ya no describe lo que se va a guardar.
    const changesCombinationOrValidity = field !== 'percentage'
    if (changesCombinationOrValidity) setConflicts([])

    // El marcador se borra apenas el valor queda bien, sin esperar a salir del campo.
    setErrors((current) => {
      const touchesValidity = field === 'validFrom' || field === 'validUntil'
      const hasRelatedError = Boolean(current[field] || current.combinations
        || (touchesValidity && (current.validity || current.validUntil))
        || (!touchesValidity && field !== 'percentage' && current.validity))
      if (!hasRelatedError) return current

      const recomputed = validate(next, isEdition)
      const updated: StockItemTypeContractFormErrors = { ...current, [field]: recomputed[field] }
      updated.combinations = undefined
      // El solapamiento y el tope de combinaciones los informa la API: cambiar la selección o las fechas los da por atendidos.
      updated.validity = undefined
      if (touchesValidity) updated.validUntil = recomputed.validUntil
      return updated
    })
  }

  function validateOnBlur(field: StockItemTypeContractFormField) {
    window.setTimeout(() => {
      const message = validate(latestForm.current, isEdition)[field]
      setErrors((current) => (current[field] === message ? current : { ...current, [field]: message }))
    }, 0)
  }

  async function handleSave() {
    if (saving) return
    const found = validate(form, isEdition)
    if (Object.keys(found).length > 0) {
      setErrors(found)
      focusFirstError(found)
      return
    }

    setSaving(true)
    try {
      const validFrom = fromDateToString(form.validFrom as Date)
      // Fecha local, sin zona horaria: toISOString() correría el día después de las 21 h.
      const validUntil = form.validUntil ? fromDateToString(form.validUntil) : null
      const percentage = form.percentage as number

      if (contract) {
        await updateStockItemTypeContract(contract.stockitemtypecontract, { percentage, validFrom, validUntil })
        onSaved(true, null)
      } else {
        const result = await createStockItemTypeContracts({
          healthEntityIds: form.healthEntityIds,
          healthEntityPlanIds: form.healthEntityPlanIds,
          stockItemTypeIds: form.stockItemTypeIds,
          contractAttentionModalityIds: form.contractAttentionModalityIds,
          percentage,
          validFrom,
          validUntil,
        })
        onSaved(false, result)
      }
    } catch (error: unknown) {
      const apiError = parseApiError(error)
      const fieldErrors = toFieldErrors(apiError)
      setConflicts(parseOverlapConflicts(apiError))
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors)
        focusFirstError(fieldErrors)
      } else if (apiError.errorCode === 'STOCK_ITEM_TYPE_CONTRACT_NOT_FOUND'
        || apiError.errorCode === 'STOCK_ITEM_TYPE_CONTRACT_INACTIVE') {
        onStale(apiError.message)
      } else {
        onError(isEdition
          ? 'No se pudieron guardar los cambios. Intentá de nuevo.'
          : 'No se pudo crear el contrato. Intentá de nuevo.')
      }
    } finally {
      setSaving(false)
    }
  }

  function fieldA11y(field: StockItemTypeContractFormField, errorId: string) {
    return errors[field] ? { 'aria-invalid': true, 'aria-describedby': errorId } : {}
  }

  const validityError = errors.validity
  const validFromInvalid = Boolean(errors.validFrom)
  const validUntilInvalid = Boolean(errors.validUntil)

  function requestLeave() {
    if (saving) return
    if (snapshotOf(form) === initialSnapshot.current) {
      onBack()
    } else {
      setConfirmingLeave(true)
    }
  }

  return (
    <div className='nv-scope nv-page stc-view stc-form-page'>
      <header className='nv-page-head'>
        <Button
          type='button'
          link
          icon='pi pi-angle-double-left'
          label='Volver'
          onClick={requestLeave}
        />
        <h1 className='nv-h1'>{isEdition ? 'Editar contrato' : 'Nuevo contrato'}</h1>
        {contract && <span className='nv-subtitle'>{contract.healthentityname}</span>}
      </header>

      <form
        className='stc-form'
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          void handleSave()
        }}
      >
        <h2 className='stc-section-title'>Alcance</h2>
        {contract ? (
          <>
            <div className='stc-row stc-row-wide'>
              <ReadOnlyData label='Obra social' value={contract.healthentityname} />
              <ReadOnlyData
                label='Plan'
                value={contract.healthentityplan == null ? 'Todos' : contract.healthentityplanname}
              />
            </div>
            <div className='stc-row stc-row-wide'>
              <ReadOnlyData
                label='Tipo de ítem'
                value={contract.stockitemtype == null ? 'Todos' : contract.stockitemtypedesc}
              />
              <ReadOnlyData
                label='Modalidad'
                value={contract.contractattentionmodality == null ? 'Todas' : contract.contractattentionmodalitydesc}
              />
            </div>
          </>
        ) : (
          <>
            <div className='stc-row stc-row-wide'>
              <div className='stc-field'>
                <label htmlFor={FIELD_INPUT_IDS.healthEntityIds}>
                  Obras sociales <span className='stc-required'>*</span>
                </label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.healthEntityIds}
                  value={form.healthEntityIds}
                  options={healthEntities}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  filter
                  filterBy='name'
                  resetFilterOnHide
                  showSelectAll
                  emptyFilterMessage='No hay obras sociales que coincidan'
                  virtualScrollerOptions={healthEntities.length > 50 ? { itemSize: 38 } : undefined}
                  placeholder='Seleccionar'
                  className={classNames('stc-chip-select', { 'p-invalid': errors.healthEntityIds })}
                  onChange={(event) => updateField('healthEntityIds', (event.value as number[] | null) ?? [])}
                  onHide={() => validateOnBlur('healthEntityIds')}
                  {...fieldA11y('healthEntityIds', 'stc-health-entities-error')}
                />
                <FieldError id='stc-health-entities-error' message={errors.healthEntityIds} />
              </div>
              <div className='stc-field'>
                <label htmlFor={FIELD_INPUT_IDS.healthEntityPlanIds}>Planes</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.healthEntityPlanIds}
                  value={form.healthEntityPlanIds}
                  options={planOptions}
                  optionLabel='label'
                  optionValue='id'
                  display='chip'
                  filter={planOptions.length > 10}
                  filterBy='label'
                  showSelectAll
                  disabled={form.healthEntityIds.length === 0 || loadingPlans}
                  placeholder={loadingPlans ? 'Cargando planes…' : 'Todos'}
                  className={classNames('stc-chip-select', { 'p-invalid': errors.healthEntityPlanIds })}
                  onChange={(event) => updateField('healthEntityPlanIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('healthEntityPlanIds', 'stc-health-entity-plans-error')}
                />
                <FieldError id='stc-health-entity-plans-error' message={errors.healthEntityPlanIds} />
              </div>
            </div>
            <div className='stc-row stc-row-wide'>
              <div className='stc-field'>
                <label htmlFor={FIELD_INPUT_IDS.stockItemTypeIds}>Tipos de ítem</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.stockItemTypeIds}
                  value={form.stockItemTypeIds}
                  options={options.stockItemTypes}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  showSelectAll
                  placeholder='Todos'
                  className={classNames('stc-chip-select', { 'p-invalid': errors.stockItemTypeIds })}
                  onChange={(event) => updateField('stockItemTypeIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('stockItemTypeIds', 'stc-stock-item-types-error')}
                />
                <FieldError id='stc-stock-item-types-error' message={errors.stockItemTypeIds} />
              </div>
              <div className='stc-field'>
                <label htmlFor={FIELD_INPUT_IDS.contractAttentionModalityIds}>Modalidades</label>
                <MultiSelect
                  inputId={FIELD_INPUT_IDS.contractAttentionModalityIds}
                  value={form.contractAttentionModalityIds}
                  options={options.modalities}
                  optionLabel='name'
                  optionValue='id'
                  display='chip'
                  showSelectAll
                  placeholder='Todas'
                  className={classNames('stc-chip-select', { 'p-invalid': errors.contractAttentionModalityIds })}
                  onChange={(event) => updateField('contractAttentionModalityIds', (event.value as number[] | null) ?? [])}
                  {...fieldA11y('contractAttentionModalityIds', 'stc-modalities-error')}
                />
                <FieldError id='stc-modalities-error' message={errors.contractAttentionModalityIds} />
              </div>
            </div>
            <span className='stc-hint' aria-live='polite'>
              <i className='pi pi-info-circle' aria-hidden='true' />{' '}
              {combinations === 1
                ? 'Se va a crear 1 contrato.'
                : `Se van a crear ${combinations} contratos, uno por cada combinación.`}
            </span>
            <FieldError id='stc-combinations-error' message={errors.combinations} />
          </>
        )}

        <h2 className='stc-section-title'>Cobertura y vigencia</h2>
        <div className='stc-row stc-row-three'>
          <div className='stc-field'>
            <label htmlFor={FIELD_INPUT_IDS.percentage}>
              Porcentaje de cobertura <span className='stc-required'>*</span>
            </label>
            <PercentageInput
              inputId={FIELD_INPUT_IDS.percentage}
              value={form.percentage}
              placeholder='Ej.: 80'
              invalid={Boolean(errors.percentage)}
              onChange={(value) => updateField('percentage', value)}
              onBlur={() => validateOnBlur('percentage')}
              {...fieldA11y('percentage', 'stc-percentage-error')}
            />
            <FieldError id='stc-percentage-error' message={errors.percentage} />
          </div>

          <div className='stc-field'>
            <label htmlFor={FIELD_INPUT_IDS.validFrom}>
              Válido desde <span className='stc-required'>*</span>
            </label>
            <Calendar
              inputId={FIELD_INPUT_IDS.validFrom}
              value={form.validFrom}
              dateFormat='dd/mm/yy'
              showIcon
              appendTo='self'
              panelClassName='stc-datepicker-below'
              placeholder='dd/mm/aaaa'
              className={validFromInvalid ? 'p-invalid' : ''}
              onChange={(event) => updateField('validFrom', event.value instanceof Date ? event.value : null)}
              onBlur={() => validateOnBlur('validFrom')}
              aria-invalid={validFromInvalid || undefined}
              aria-describedby={errors.validFrom ? 'stc-valid-from-error' : undefined}
            />
            <FieldError id='stc-valid-from-error' message={errors.validFrom} />
          </div>

          <div className='stc-field'>
            <label htmlFor={FIELD_INPUT_IDS.validUntil}>Válido hasta</label>
            <Calendar
              inputId={FIELD_INPUT_IDS.validUntil}
              value={form.validUntil}
              dateFormat='dd/mm/yy'
              showIcon
              appendTo='self'
              panelClassName='stc-datepicker-below'
              showButtonBar
              minDate={form.validFrom ?? undefined}
              placeholder='Sin fecha de fin'
              className={validUntilInvalid ? 'p-invalid' : ''}
              onChange={(event) => updateField('validUntil', event.value instanceof Date ? event.value : null)}
              onBlur={() => validateOnBlur('validUntil')}
              aria-invalid={validUntilInvalid || undefined}
              aria-describedby={errors.validUntil ? 'stc-valid-until-error' : undefined}
            />
            <FieldError id='stc-valid-until-error' message={errors.validUntil} />
          </div>
        </div>

        {validityError && (
          <div id='stc-validity-error'>
            <Alert
              color='yellow'
              icon='pi-exclamation-triangle'
              className='w-fit py-2 px-3'
              contentClassName='flex align-items-center'
            >
              <span className='text-yellow-700 m-0 text-l font-medium'>
                Ya existen contratos con la misma configuración para el rango de fechas ingresado
              </span>
              {conflicts.length > 0 && (
                <Button
                  type='button'
                  text
                  rounded
                  icon='pi pi-eye'
                  className='stc-alert-action'
                  aria-label='Ver contratos existentes'
                  tooltip='Ver contratos existentes'
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => setViewingConflicts(true)}
                />
              )}
            </Alert>
          </div>
        )}

        <div className='stc-form-actions'>
          <Button
            type='button'
            className='button stc-ghost-button'
            label='Cancelar'
            disabled={saving}
            onClick={requestLeave}
          />
          <Button
            type='submit'
            className='buttonMain'
            label={saving ? 'Guardando…' : (isEdition ? 'Guardar cambios' : 'Crear')}
            loading={saving}
          />
        </div>
      </form>

      <OverlappingContractsModal
        visible={viewingConflicts}
        contracts={conflicts}
        onClose={() => setViewingConflicts(false)}
      />

      <Modal
        visible={confirmingLeave}
        setVisible={setConfirmingLeave}
        header={<span className='stc-modal-title'>¿Salir sin guardar?</span>}
        width='w-medium'
        className='stc-modal'
        footerButtonLeftText='Seguir editando'
        onClickLeftBtn={() => setConfirmingLeave(false)}
        footerButtonRightText='Salir sin guardar'
        onClickRightBtn={() => {
          setConfirmingLeave(false)
          onBack()
        }}
      >
        <p className='stc-confirm-text'>Los cambios que hiciste se van a perder.</p>
      </Modal>
    </div>
  )
}
